interface ModalSize {
  width: string | number;
  height: string | number;
}

interface StoredModalSize {
  width: number;
  height: number;
  timestamp: number;
}

const MOBILE_BREAKPOINT = 768;

// Size presets
const SIZE_PRESETS = {
  small: {
    width: 600,
    height: 'auto',
    maxWidth: 600,
  },
  medium: {
    width: 900,
    height: 'auto',
    maxWidth: 900,
  },
  large: {
    width: '75%',
    height: '90%',
    maxWidth: '85%',
  },
};

export default {
  /**
   * Check if the current viewport is mobile
   */
  isMobile(): boolean {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  },

  /**
   * Get default modal size based on viewport and size preset
   */
  getDefaultSize(size: 'small' | 'medium' | 'large' = 'medium'): ModalSize {
    if (this.isMobile()) {
      return {
        width: '100%',
        height: '100%',
      };
    }

    const preset = SIZE_PRESETS[size];

    // For large size, use percentages; for small/medium use fixed px values
    if (size === 'large') {
      return {
        width: preset.width,
        height: preset.height,
      };
    }

    // For small and medium, return the px values directly
    return {
      width: preset.width,
      height: preset.height,
    };
  },

  /**
   * Get stored modal size from localStorage
   */
  getStoredSize(modalName: string): ModalSize | null {
    try {
      const key = `modal-size-${modalName}`;
      const stored = localStorage.getItem(key);

      if (!stored) {
        return null;
      }

      const parsed: StoredModalSize = JSON.parse(stored);

      // Return stored size if it exists and is valid
      if (
        parsed &&
        typeof parsed.width === 'number' &&
        typeof parsed.height === 'number'
      ) {
        return {
          width: parsed.width,
          height: parsed.height,
        };
      }

      return null;
    } catch (error) {
      console.error('Error reading stored modal size:', error);
      return null;
    }
  },

  /**
   * Save modal size to localStorage
   */
  saveSize(modalName: string, width: number, height: number): void {
    try {
      const key = `modal-size-${modalName}`;
      const data: StoredModalSize = {
        width,
        height,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving modal size:', error);
    }
  },

  /**
   * Reset modal size (clear from localStorage)
   */
  resetSize(modalName: string): void {
    try {
      const key = `modal-size-${modalName}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error resetting modal size:', error);
    }
  },

  /**
   * Get modal size (stored or default)
   */
  getModalSize(
    modalName: string,
    size: 'small' | 'medium' | 'large' = 'medium',
  ): ModalSize {
    // On mobile, always return 100%
    if (this.isMobile()) {
      return {
        width: '100%',
        height: '100%',
      };
    }

    // Check for stored size
    const storedSize = this.getStoredSize(modalName);
    if (storedSize) {
      return storedSize;
    }

    // Return default size based on preset
    return this.getDefaultSize(size);
  },

  /**
   * Convert size to pixels if percentage, otherwise return as is
   */
  normalizeSize(size: string | number): number {
    if (typeof size === 'number') {
      return size;
    }

    // If it's a percentage, calculate pixel value
    if (size.includes('%')) {
      const percent = parseFloat(size);
      return (window.innerWidth * percent) / 100;
    }

    // If it's already pixels, parse it
    return parseFloat(size);
  },

  /**
   * Constrain size within reasonable bounds
   */
  constrainSize(
    width: number,
    height: number,
  ): { width: number; height: number } {
    const minWidth = 400;
    const minHeight = 300;
    const maxWidth = window.innerWidth;
    const maxHeight = window.innerHeight;

    return {
      width: Math.max(minWidth, Math.min(width, maxWidth)),
      height: Math.max(minHeight, Math.min(height, maxHeight)),
    };
  },
};
